import React from 'react';
import { Helmet } from 'react-helmet';
import { RouteComponentProps, navigate } from '@reach/router';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import {
  addProjectTemplate,
  addProjectTemplateFn,
  getProject,
  getProjectFn,
  removeProjectTemplate,
  removeProjectTemplateFn,
} from 'store/companies/companiesSlice';
import { ITasks, IUsers, RootState } from 'store/store';
import ProjectTemplateView from './ProjectTemplateView';
import LogoLoading from 'components/LogoLoading';
import { display } from 'components/ErrorView/ErrorDialog';
import { projectRoutesName } from 'components/MainHeader/Messages';
import { Box } from 'utils/style/extendable';
import { appDispatch } from 'store/store';
import {
  ICompany,
  IProjectStore,
  IProjectTemplate,
} from 'store/companies/companiesTypes';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { isCompanyAdmin } from 'store/companies/companiesUtils';
import i18n from '../../../i18n';
import { black } from 'utils/style/colors';

interface IStateToProps {
  tasks: ITasks;
  users: IUsers;
  company: ICompany;
  project: IProjectStore;
  thisUser: IThisUserState;
  companyTemplates: string[];
}

interface IDispatchToProps {
  getProject: (
    projectId: number,
    companyId: number
  ) => ReturnType<typeof getProjectFn>;
  addProjectTemplate: (
    projectTemplate: IProjectTemplate,
    companyId: number
  ) => ReturnType<typeof addProjectTemplateFn>;
  removeProjectTemplate: (
    projectTemplate: IProjectTemplate,
    companyId: number
  ) => ReturnType<typeof removeProjectTemplateFn>;
}

interface IOwnProps {
  companyId?: string;
  projectId?: string;
}

type IComponentProps = IOwnProps &
  IStateToProps &
  IDispatchToProps &
  RouteComponentProps;

interface IComponentStates {
  loading: boolean;
}

class ProjectTemplates extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      loading: false,
    };
  }

  componentDidMount() {
    const isAdmin = isCompanyAdmin(this.props.thisUser, this.props.company);
    if (!isAdmin) {
      navigate('/notfound');
    }
    const projectId = Number(this.props.projectId);
    this.props.getProject(projectId, this.props.company.id);
  }

  handleSelectTemplate = (templateId: number, selected: boolean) => {
    const projectTemplate = {
      taskId: templateId,
      projectId: Number(this.props.projectId),
    } as IProjectTemplate;
    if (selected) {
      this.props
        .addProjectTemplate(projectTemplate, this.props.company.id)
        .catch((err: any) => {
          display(err);
        });
    } else {
      this.props
        .removeProjectTemplate(projectTemplate, this.props.company.id)
        .catch((err: any) => {
          display(err);
        });
    }
  };

  handleUpdateTemplate = (template: IProjectTemplate) => {
    return this.props
      .addProjectTemplate(template, this.props.company.id)
      .catch((err: any) => {
        display(err);
      });
  };

  render() {
    const { companyTemplates, project, tasks, users, company } = this.props;
    if (project.stub) {
      return <LogoLoading />;
    }
    const projectName = project && project.name;
    const title = projectRoutesName('templates', projectName);
    return (
      <React.Fragment>
        <Helmet>
          <title>{title}</title>
          <meta name="description" content={title} />
        </Helmet>

        <h5>{title}</h5>
        <p style={{ color: black }}>
          {i18n.t('chooseTemplates', { ns: 'companyManagement' }) as string}
        </p>
        <Box className="row" style={{ overflow: 'auto' }}>
          {companyTemplates
            .sort((a, b) => {
              if (tasks.data[b].taskName! < tasks.data[a].taskName!) {
                return 1;
              }
              return 0;
            })
            .map((templateId) => (
              <ProjectTemplateView
                key={templateId}
                template={tasks.data[templateId]}
                onSelectTemplate={this.handleSelectTemplate}
                onUpdateTemplate={this.handleUpdateTemplate}
                users={users}
                company={company}
                current={project.templates.find(
                  (templateObject) =>
                    templateObject.taskId.toString() === templateId
                )}
              />
            ))}
        </Box>
      </React.Fragment>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const projectId = Number(props.projectId);
  const companyId = Number(props.companyId);
  const companyTemplates = Object.keys(
    state.companies.data[companyId].templates
  );
  const company = state.companies.data[companyId];
  const project = company.projects[projectId];
  const tasks = state.tasks;
  const thisUser = state.thisUser;
  const users = state.users;
  return { companyTemplates, project, tasks, company, thisUser, users };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => {
  return {
    getProject: (projectId: number, companyId: number) =>
      appDispatch(getProject({ projectId, companyId })),
    addProjectTemplate: (
      projectTemplate: IProjectTemplate,
      companyId: number
    ) => appDispatch(addProjectTemplate({ projectTemplate, companyId })),
    removeProjectTemplate: (
      projectTemplate: IProjectTemplate,
      companyId: number
    ) => appDispatch(removeProjectTemplate({ projectTemplate, companyId })),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectTemplates);
